import React, { useEffect, useRef } from 'react';
import { Modal, Button } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Formik, Form as FormikForm } from 'formik';
import * as Yup from 'yup';

import { FormGroupControl, FormGroupSelect } from '@components/Base';
import { ClaimStatuses } from '@models/InsuranceClaim';
import i18n from '@components/i18n';

import { insuranceClaimQuickUpdate } from './action';
import { selectInsuranceClaimEditModalReducer } from './selector';

export interface ClaimEditModalProps {
  isOpen: boolean;
  onClose: () => void;
  claimNumber: string;
  claimableType: string;
  claimableId: number;
  currentStatus: ClaimStatuses;
  currentNote?: string;
  onSuccess?: () => void;
}

interface FormValues {
  status: ClaimStatuses;
  note: string;
}

const groupProps = { className: 'mb-3' };

const validationSchema = Yup.object({
  status: Yup.string().required(i18n.t('common:required')),
  note: Yup.string(),
});

const InsuranceClaimEditModal = ({
  isOpen,
  onClose,
  claimNumber,
  claimableType,
  claimableId,
  currentStatus,
  currentNote = '',
  onSuccess,
}: ClaimEditModalProps) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { isUpdating, lastSuccessClaimableId } = useSelector(selectInsuranceClaimEditModalReducer);
  const hasTriggeredSuccess = useRef(false);

  const initialValues: FormValues = {
    status: currentStatus,
    note: currentNote,
  };

  useEffect(() => {
    if (isOpen && lastSuccessClaimableId === claimableId && !hasTriggeredSuccess.current) {
      hasTriggeredSuccess.current = true;
      onClose();
      if (onSuccess) {
        onSuccess();
      }
    }
  }, [isOpen, lastSuccessClaimableId, claimableId, onClose, onSuccess]);

  useEffect(() => {
    if (!isOpen) {
      hasTriggeredSuccess.current = false;
    }
  }, [isOpen]);

  const handleSubmit = (values: FormValues) => {
    dispatch(
      insuranceClaimQuickUpdate({
        claimableType,
        claimableId,
        status: values.status,
        note: values.note,
      }),
    );
  };

  return (
    <Modal
      show={isOpen}
      onHide={onClose}
      aria-labelledby="claim-edit-modal-title"
      aria-describedby="claim-edit-modal-description"
      centered
    >
      <Formik
        enableReinitialize
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {({ isValid }) => (
          <FormikForm>
            <Modal.Header closeButton>
              <Modal.Title id="claim-edit-modal-title">{t('claimEditModal.title', { claimNumber })}</Modal.Title>
            </Modal.Header>
            <Modal.Body id="claim-edit-modal-description">
              <FormGroupSelect name="status" label={t('common:status')} groupProps={groupProps}>
                <option value={ClaimStatuses.Pending}>{t('insuranceClaimStatuses.pending')}</option>
                <option value={ClaimStatuses.Voided}>{t('insuranceClaimStatuses.voided')}</option>
              </FormGroupSelect>

              <FormGroupControl
                name="note"
                label={t('common:note')}
                groupProps={groupProps}
                as="textarea"
                rows={3}
                placeholder={t('claimEditModal.note.placeholder')}
              />
            </Modal.Body>
            <Modal.Footer>
              <Button variant="secondary" onClick={onClose} disabled={isUpdating}>
                {t('common:cancel')}
              </Button>
              <Button type="submit" disabled={isUpdating || !isValid}>
                {isUpdating ? t('common:saving') : t('common:save')}
              </Button>
            </Modal.Footer>
          </FormikForm>
        )}
      </Formik>
    </Modal>
  );
};

export { InsuranceClaimEditModal };
